import { milesToMeters } from '@/components/shared/x-distance.vue'
import { acresToSqMeters } from '@/lib/area'
import { FIELDS } from '@/fish/lib/taxonomy-constants'
import { WaterbodyAttribute } from '@onxmaps/supergraph'
import type { WaterbodyFilter } from '@onxmaps/supergraph'

const DEFAULT_RADIUS_MILES = 50

/**
 * The four fish-attribute dimensions the map-filter UI's species toggles
 * expose (`species-filter.vue`), matching `WaterbodyFilter`'s parallel
 * `trophyFish`/`abundantFish`/`keeperFish`/`highStockingEffortFish` fields.
 */
export type PotentialCategory =
  | 'trophy'
  | 'abundant'
  | 'keeper'
  | 'highStockingEffort'

/** Maps a `PotentialCategory` to its `WaterbodyFilter`/graph field name. */
export const POTENTIAL_FIELDS: Record<PotentialCategory, string> = {
  trophy: FIELDS.TROPHY_FISH,
  abundant: FIELDS.ABUNDANT_FISH,
  keeper: FIELDS.KEEPER_FISH,
  highStockingEffort: FIELDS.STOCKING_EFFORT,
}

export interface WaterbodyQueryArgs {
  species?: string[]
  /** Which fish-attribute dimension(s) matter for this search — omit for a plain species-presence match. */
  potential?: PotentialCategory[]
  near?: { lat: number; lon: number; radiusMiles?: number }
  minAreaAcres?: number
  maxAreaAcres?: number
}

/** Builds a `WaterbodyFilter` from the `queryWaterbodies` tool's model-supplied args, scoped to `Lake`. */
export const buildWaterbodyFilter = ({
  species,
  potential,
  near,
  minAreaAcres,
  maxAreaAcres,
}: WaterbodyQueryArgs): WaterbodyFilter => {
  // Matches the map-filter UI's bbox queries (getWaterbodiesByBBox /
  // getWaterbodyIDsByBbox): excludes low-value "Unnamed Lake" results that
  // have no fish survey data behind them.
  const filter: WaterbodyFilter = {
    typenames: ['Lake'],
    attributes: { contains: WaterbodyAttribute.FishSurveyData },
  }

  if (species?.length) {
    if (potential?.length) {
      // One filter clause per requested category, ANDed together (siblings
      // in a GraphQL filter object) — matches the map-filter UI's behavior
      // when multiple toggles are active simultaneously.
      for (const category of potential) {
        ;(filter as Record<string, unknown>)[POTENTIAL_FIELDS[category]] = {
          slug: { in: species },
        }
      }
    } else {
      filter.fishTypes = { slug: { in: species } }
    }
  }

  if (near) {
    filter.location = {
      near: {
        lat: near.lat,
        lon: near.lon,
        distance: milesToMeters(near.radiusMiles ?? DEFAULT_RADIUS_MILES),
      },
    }
  }

  if (minAreaAcres !== undefined && maxAreaAcres !== undefined) {
    filter.area = {
      between: {
        min: acresToSqMeters(minAreaAcres),
        max: acresToSqMeters(maxAreaAcres),
      },
    }
  } else if (minAreaAcres !== undefined) {
    filter.area = { gt: acresToSqMeters(minAreaAcres) }
  } else if (maxAreaAcres !== undefined) {
    filter.area = { lt: acresToSqMeters(maxAreaAcres) }
  }

  return filter
}
